/**
 * GoogleOAuthSettingsPage - Admin settings for Google OAuth configuration
 * Allows switching between Emergent-managed and custom Google OAuth
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, Save, Loader2, Shield, ExternalLink, Info, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Google Icon SVG
const GoogleIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const GoogleOAuthSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    use_custom: false,
    client_id: '',
    client_secret: '',
    auto_create_users: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/settings/google-oauth`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Load settings error:', error);
      // Use defaults if not configured yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/settings/google-oauth`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Google OAuth Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/portal-settings')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zu Portal-Einstellungen
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <KeyRound className="h-6 w-6 text-red-600" />
          </div>
          Google Login Einstellungen
        </h1>
        <p className="text-gray-600 mt-2">
          Ermöglichen Sie Kunden die Anmeldung mit ihrem Google-Konto
        </p>
      </div>

      <div className="space-y-6">
        {/* Enable/Disable */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GoogleIcon size={24} />
              Google-Anmeldung
            </CardTitle>
            <CardDescription>
              Kunden können sich mit einem Klick mit ihrem Google-Konto anmelden
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Google Login aktivieren</Label>
                <p className="text-sm text-gray-500">
                  Zeigt den &quot;Mit Google anmelden&quot; Button auf der Login-Seite
                </p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <Label className="text-base font-medium">Automatische Kontoerstellung</Label>
                <p className="text-sm text-gray-500">
                  Erstellt automatisch ein Konto für neue Kunden beim ersten Google-Login
                </p>
              </div>
              <Switch
                checked={settings.auto_create_users}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_create_users: checked })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Provider Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              OAuth-Provider
            </CardTitle>
            <CardDescription>
              Wählen Sie zwischen der integrierten Lösung oder eigenen Google API-Zugangsdaten
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Emergent Auth Option */}
            <div 
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${!settings.use_custom ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
              onClick={() => setSettings({ ...settings, use_custom: false })}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-full ${!settings.use_custom ? 'bg-green-500' : 'bg-gray-200'}`}>
                  {!settings.use_custom ? (
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  ) : (
                    <div className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Logikwerk Auth (Empfohlen)</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Integrierte Google-Anmeldung - keine Konfiguration erforderlich.
                    Funktioniert sofort nach Aktivierung.
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Sofort einsatzbereit</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Google OAuth Option */}
            <div 
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${settings.use_custom ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
              onClick={() => setSettings({ ...settings, use_custom: true })}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-full ${settings.use_custom ? 'bg-blue-500' : 'bg-gray-200'}`}>
                  {settings.use_custom ? (
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  ) : (
                    <div className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Eigene Google API-Zugangsdaten</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Verwenden Sie Ihre eigenen Google OAuth Client-ID und Secret.
                    Ideal für Branding und vollständige Kontrolle.
                  </p>
                </div>
              </div>
            </div>

            {/* Custom OAuth Form */}
            {settings.use_custom && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <strong>So erhalten Sie Ihre Google API-Zugangsdaten:</strong>
                    <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                      <li>Öffnen Sie die <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console <ExternalLink className="h-3 w-3 inline" /></a></li>
                      <li>Erstellen Sie ein neues Projekt (oder wählen Sie ein bestehendes)</li>
                      <li>Gehen Sie zu &quot;APIs &amp; Services&quot; → &quot;Credentials&quot;</li>
                      <li>Klicken Sie auf &quot;Create Credentials&quot; → &quot;OAuth client ID&quot;</li>
                      <li>Wählen Sie &quot;Web application&quot; als Anwendungstyp</li>
                      <li>Fügen Sie Ihre Domain als &quot;Authorized JavaScript origins&quot; hinzu</li>
                      <li>Fügen Sie die Redirect-URI hinzu: <code className="bg-gray-200 px-1 rounded">{window.location.origin}/portal/google/callback</code></li>
                      <li>Kopieren Sie Client-ID und Client Secret</li>
                    </ol>
                  </AlertDescription>
                </Alert>

                <div>
                  <Label htmlFor="client_id">Google Client ID</Label>
                  <Input
                    id="client_id"
                    value={settings.client_id || ''}
                    onChange={(e) => setSettings({ ...settings, client_id: e.target.value })}
                    placeholder="123456789-abc123.apps.googleusercontent.com"
                    className="mt-1 font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="client_secret">Google Client Secret</Label>
                  <Input
                    id="client_secret"
                    type="password"
                    value={settings.client_secret || ''}
                    onChange={(e) => setSettings({ ...settings, client_secret: e.target.value })}
                    placeholder="GOCSPX-..."
                    className="mt-1 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Wird verschlüsselt gespeichert und nicht im Klartext angezeigt
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Einstellungen speichern
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoogleOAuthSettingsPage;
